import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  useToast,
} from "@chakra-ui/react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { EditOrganizationRequest, useUpdateOrganizationMutation } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";

interface Props {
  currentValue: string;
  fieldName: string;
  label: string;
  helperText: string;
  toastMessage: string;
  buildPatch: (value: string | null) => Partial<EditOrganizationRequest>;
  validation?: RegisterOptions;
}

interface FormValues {
  field: string;
}

export const ChangeOrgTextField = ({
  currentValue,
  fieldName,
  label,
  helperText,
  toastMessage,
  buildPatch,
  validation,
}: Props) => {
  const toast = useToast();
  const authState = useLoggedInWithOrgContextState();
  const [editOrg, { isLoading }] = useUpdateOrganizationMutation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const displayer = useApiError();

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      field: currentValue,
    },
  });

  const value = watch("field");

  const onSubmit = async (values: FormValues) => {
    const response = await editOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: buildPatch(values.field || null),
    });
    if ("data" in response) {
      dispatch(updateSelectedOrg(response.data));
      toast({ title: toastMessage, status: "success" });
    } else {
      displayer.trigger(response.error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl mt="15px" isInvalid={errors.field !== undefined}>
        <FormLabel htmlFor={fieldName}>{label}</FormLabel>
        <Input id={fieldName} type="text" {...register("field", validation)} />
        <FormErrorMessage>{errors.field && errors.field.message}</FormErrorMessage>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
      {currentValue.trim() !== value.trim() && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant={"solid"}
          colorScheme="teal"
          size={"sm"}
          isLoading={isLoading}
          type="submit"
        >
          {t("Save change")}
        </Button>
      )}
    </form>
  );
};
